import {Component} from "@angular/core";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {SiteService} from "./site.service";
import {ImportK2SitesDialogComponent} from "./import-k2-sites-dialog.component";
import {RouteHelper, RouteMapping} from "../misc/route-helper";
import {UploaderDialogResult} from "../uploader-wrapper/uploader-dialog.model";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {SiteCreateDialogComponent} from "./site-create-dialog.component";
import {handleErrors} from "../misc/handle-errors.decorator";
import {ProjectService} from "../project/project.service";
import {ProjectModel, projectModelFactory} from "../project/project.model";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Component({
  templateUrl: "./site-list.template.pug",
  providers: [
    SiteService,
    DialogService,
  ],
})
export class SiteListComponent extends RouteHelper {
  sites: any = [];
  k2Sites: any = [];
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number;
  totalCount: number = 0;
  searchText: string = "";
  error: Error;
  projectId: string;
  isMigrationActive: boolean = false;
  loading: boolean = true;

  paramsMap: RouteMapping[] = [
    {fieldName: "currentPage", paramName: "page", defaultValue: DEFAULT_PAGE, type: "number"} as RouteMapping,
    {fieldName: "pageSize", paramName: "limit", defaultValue: DEFAULT_PAGE_LIMIT, type: "number"} as RouteMapping,
    {fieldName: "searchText", paramName: "search"} as RouteMapping,
    {fieldName: "projectId", paramName: "projectId"} as RouteMapping,
  ];

  constructor(
    private siteService: SiteService,
    private projectService: ProjectService,
    public route: ActivatedRoute,
    public router: Router,
    public dialogService: DialogService,
  ) {
    super();
  }

  navigate(queryParams: Params, skipLocationChange: boolean = false): Promise<boolean> {
    return this.router.navigate(["sites"], {queryParams, skipLocationChange});
  }

  @handleErrors()
  async loadData() {
    if (!this.projectId) {
      throw new Error("Please go back to the main page and try again.");
    }

    const {rows, count} = await this.siteService.getSites(
      +(this.currentPage) - 1,
      +(this.pageSize),
      this.searchText,
      this.projectId,
    );

    if (rows.length) {
      this.isMigrationActive = rows[0].isMigrationActive;
    } else {
      let project: ProjectModel = projectModelFactory();
      project = await this.projectService.getProjectById(this.projectId).toPromise();
      this.isMigrationActive = project.isMigrationActive;
    }

    this.sites = rows;
    this.maxPages = Math.ceil(count / (+this.pageSize));
    this.totalCount = count;

    this.loading = false;
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }

    this.syncRoute();
  }

  uploadModalClosed(event: any): void {
    const result = event["value"] as UploaderDialogResult;

    if (result.responses.length) {
      this.loadData();
    }
  }

  @handleErrors()
  async create() {
    this.loading = true;
    const result = await SiteCreateDialogComponent.showCreateDialog(this.dialogService, {projectId: this.projectId});

    if (result) {
      this.loadData();
    } else {
      this.loading = false;
    }
  }

  async addK2Sites(): Promise<any> {
    const result = await ImportK2SitesDialogComponent.showImportK2SitesDialogModal(
      this.dialogService,
      {projectId: this.projectId},
    );

    if (!result) return;

    this.loadData();
  }
}
